/**
 * CommandPalette — Global search overlay triggered by Cmd+K.
 *
 * Finder/Spotlight-style rows: each station result carries right-aligned action
 * icons — Observations, Station info, Availability — that reveal on hover/active
 * and glide with a single sliding highlight bar (Apple-like continuity). Typing a
 * network / data-source name surfaces a Network result that routes to /network.
 *
 * Keyboard model:
 *   ↑ / ↓   move between rows (resets the focused action to the first)
 *   ⤥ / ⇧⤥  cycle the focused action within the active row (does not fight the
 *           text caret, unlike ← / →)
 *   ↵       run the focused action
 *   Esc     close
 *
 * Requirements: 7.1, 7.4
 */

import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/appStore';
import { useLiquidGlass } from '../../hooks/useLiquidGlass';
import { Search } from '../icons';
import { prefersReducedMotion } from '../../utils/accessibility';
import { fetchJSON } from '../../utils/fetchClient';
import './CommandPalette.css';

const MAX_RESULTS = 12;
const MAX_NETWORK_RESULTS = 3;
/** Debounce before the palette calls the server search. Results now paint INSTANTLY
 *  from the in-memory bundle (see the search effect), so this debounce only governs
 *  the background revalidation request — it no longer sits on the critical path the
 *  user waits on. Long enough to coalesce a fast typist's keystrokes into one call. */
const SEARCH_DEBOUNCE_MS = 150;
/** Session result cache cap (trimmed-query → authoritative server results). Bounds
 *  memory while covering the backspace/re-type churn of a single search session. */
const QUERY_CACHE_MAX = 50;

/** Search lifecycle for the current query. `error` (endpoint failed AND the
 *  client bundle can't answer) is deliberately distinct from a `ready` empty
 *  result set, so the UI can offer a Retry instead of "no matches". */
type SearchStatus = 'idle' | 'loading' | 'ready' | 'error';

/** Shape returned by GET /api/station-metadata/search. */
interface SearchResponse {
  success?: boolean;
  query?: string;
  stations?: Array<{ station_code: string; name?: string; parameters?: string[]; dataSources?: string[] }>;
  networks?: Array<{ id: string; count: number }>;
}

type ResultKind = 'station' | 'network' | 'recent' | 'favorite';
type ActionKey = 'observations' | 'station' | 'availability' | 'network' | 'search';

interface PaletteAction {
  key: ActionKey;
  /** Human label — used for tooltip + aria-label + footer hints. */
  label: string;
  /** In-app route (same tab). */
  path: string;
}

/** A named, human-facing station identifier shown as plain labeled text in a row
 *  (e.g. { label: 'WMO', value: '03772' } → "WMO: 03772"). */
interface StationTagId {
  label: string;
  value: string;
}

interface PaletteResult {
  id: string;
  label: string;
  sublabel?: string;
  kind: ResultKind;
  /** Named identifiers (WMO/ICAO/IATA/mgID/MMI) rendered as plain text under the
   *  row so the user can tell rows apart and know what they're clicking. */
  identifiers?: StationTagId[];
  /** Ordered actions; actions[0] is the default (row-body click / Enter). */
  actions: PaletteAction[];
}

/** Source-agnostic station shape — built the same way whether it came from the
 *  server /search endpoint (station_code) or the client bundle (stationCode). */
interface StationHit {
  code: string;
  name: string;
  parameters: string[];
}

/** A row of the in-memory client search index (mirror of the server's). Codes and
 *  names are pre-uppercased so a per-keystroke scan is a run of cheap compares. */
interface ClientStationRow {
  code: string;
  codeU: string;
  name: string;
  nameU: string;
  parameters: string[];
  /** Named identifiers (WMO/ICAO/IATA/mgID/MMI) — shown as plain text in the row AND
   *  searchable. */
  tagIds: StationTagId[];
  /** Every alternate identifier UPPERCASED — the tag values above PLUS each device's
   *  station id / source station id — for matching only (device ids aren't displayed:
   *  there are many and they're network-internal). */
  searchIdsU: string[];
}

interface ClientIndex {
  stations: ClientStationRow[];
  /** code (UPPERCASED) → row, so a server result can reuse the row's identifiers. */
  byCode: Map<string, ClientStationRow>;
  networks: Array<{ id: string; idU: string; count: number }>;
}

/** Build a Network result that routes to that data-source's quality view. */
const buildNetworkResult = (id: string, count: number): PaletteResult => ({
  id: `net-${id}`,
  label: id,
  sublabel: `Network · ${count.toLocaleString()} station${count === 1 ? '' : 's'}`,
  kind: 'network',
  actions: [{ key: 'network', label: 'Network quality', path: `/quality/network/${encodeURIComponent(id)}` }],
});

/** Build a full station result carrying all three action deep-links. `identifiers`
 *  (WMO/ICAO/mgID/…) are rendered as plain labeled text under the row so the user can
 *  tell rows apart at a glance. */
const buildStationResult = (
  hit: StationHit,
  kind: 'station' | 'favorite' = 'station',
  identifiers?: StationTagId[],
): PaletteResult => {
  const enc = encodeURIComponent(hit.code);
  const params = hit.parameters.filter(Boolean);
  const availabilityPath = params.length
    ? `/availability?stationCodes=${enc}&parameters=${encodeURIComponent(params.join(','))}`
    : `/availability?stationCodes=${enc}`;
  const sublabel = kind === 'favorite'
    ? (hit.name ? `${hit.name} · Favorite` : 'Favorite')
    : hit.name || undefined;
  return {
    id: hit.code,
    label: hit.code,
    sublabel,
    kind,
    identifiers: identifiers?.length ? identifiers : undefined,
    actions: [
      { key: 'observations', label: 'Observations', path: `/station/${enc}/observations` },
      { key: 'station', label: 'Station info', path: `/station/${enc}` },
      { key: 'availability', label: 'Availability', path: availabilityPath },
    ],
  };
};

/** Tag keys that carry a searchable + displayable station identifier, with the human
 *  label shown as plain text in the row. Casing mirrors the app's TAG_LABELS
 *  (TableView/LocationCard) so ⌘K reads the same as the tables. */
const ID_TAG_KEYS: Array<{ key: string; label: string }> = [
  { key: 'wmo', label: 'WMO' },
  { key: 'icao', label: 'ICAO' },
  { key: 'iata', label: 'IATA' },
  { key: 'mgID', label: 'mgID' },
  { key: 'mmi', label: 'MMI' },
];

/** Coerce a possibly non-string tag value to a display string (API passthrough
 *  can yield non-strings — calling .toUpperCase() on one crashed the palette). */
const toName = (raw: unknown): string =>
  typeof raw === 'string' ? raw : raw != null ? String(raw) : '';

/**
 * Score a candidate (already-uppercased) against an uppercased needle. Higher is
 * better; -1 = no match. Exact > prefix > earliest-substring — a BYTE-FOR-BYTE
 * mirror of the server's scoreMatch (station-metadata.js), so the instant local
 * results are ranked the same way the server would rank them. That parity is what
 * lets the local preview and the server revalidation agree: the list doesn't
 * reshuffle when the (slower) authoritative response lands on top of it.
 */
const scoreMatch = (haystackUpper: string, needleUpper: string): number => {
  if (!haystackUpper) return -1;
  if (haystackUpper === needleUpper) return 100;
  if (haystackUpper.startsWith(needleUpper)) return 80;
  const idx = haystackUpper.indexOf(needleUpper);
  if (idx === -1) return -1;
  return Math.max(10, 60 - idx); // earlier hit ranks higher
};

/** Exit-burst duration — must match the .cmd-palette.is-closing animation (CSS). */
const CLOSE_MS = 220;

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  // `closing` keeps the palette mounted through its exit burst before it unmounts.
  const [closing, setClosing] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PaletteResult[]>([]);
  const [status, setStatus] = useState<SearchStatus>('idle');
  const [activeIndex, setActiveIndex] = useState(0);
  const [actionIndex, setActionIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  // Selector-scoped: this palette is mounted once for the app's whole lifetime
  // (App.tsx), so a bare useAppStore() would re-run it on EVERY store mutation
  // app-wide (nav, filters, boundaries, selection, loading toggles) — most of them
  // during the busy initial-boot window. Subscribe to just `allStations` so the
  // closed palette stays inert while the rest of the app boots.
  const allStations = useAppStore((s) => s.allStations);

  // Liquid Glass refraction on the floating panel. The palette rides over the
  // scrim + page content, so its edges have a real backdrop to lens. `enabled`
  // is bound to `open` so the hook's effect fires exactly when the panel mounts
  // and tears the filter down on close. On Safari/Firefox/jsdom and under
  // reduced-transparency / high-contrast / Calm mode the hook stays inert and the
  // CSS `.cmd-palette` glass is the fallback (solid under the a11y floor — see
  // CommandPalette.css). The teardrop-drop entrance animates transform on THIS
  // node, which can perturb the refraction sample mid-flight; that's fine —
  // refraction is delight, not function, and the base CSS glass carries legibility
  // throughout, so the effect simply settles once the drop reaches rest.
  const { ref: glassRef } = useLiquidGlass<HTMLDivElement>({ enabled: open, radius: 28 });

  // Dismiss with the exit burst: flag `closing` (CSS plays the pop), then unmount
  // after the animation. Only ever called while open (overlay/Esc/⌘K toggle).
  // Navigation-driven closes skip the burst (the route is already changing) by
  // unmounting immediately via setOpen(false).
  const requestClose = useCallback(() => {
    setClosing(true);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setClosing(false);
      setOpen(false);
    }, CLOSE_MS);
  }, []);

  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  // Open (or re-open) — cancels any in-flight exit burst so a fast reopen snaps
  // back to a clean, fully-open palette instead of finishing the pop.
  const openPalette = useCallback(() => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setClosing(false);
    setOpen(true);
  }, []);

  // Global Cmd+K / Ctrl+K listener
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (open && !closing) requestClose();
        else openPalette();
      }
      if (e.key === 'Escape' && open && !closing) {
        requestClose();
      }
    };
    document.addEventListener('keydown', handler);
    // The Instrument Bezel's visible "Search or go to…" field dispatches this so
    // the field is a real, discoverable trigger for the same palette as Cmd+K.
    window.addEventListener('meridian:open-command-palette', openPalette);
    return () => {
      document.removeEventListener('keydown', handler);
      window.removeEventListener('meridian:open-command-palette', openPalette);
    };
  }, [open, closing, requestClose, openPalette]);

  // Focus input when opening
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setStatus('idle');
      setActiveIndex(0);
      setActionIndex(0);
      // ponytail: small delay for animation frame to render input
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Hand-off flag for the InstrumentBezel trigger: while the palette is open the
  // bezel's centered "Search or go to…" field recedes (CSS reads this attribute)
  // so the floating field visibly takes its place.
  useEffect(() => {
    const body = document.body;
    if (open) body.setAttribute('data-command-palette-open', '');
    else body.removeAttribute('data-command-palette-open');
    return () => body.removeAttribute('data-command-palette-open');
  }, [open]);

  // ── Client search index (powers instant results + the offline fallback) ──────
  // The app already holds every station in memory (loadAllStations, App.tsx). We
  // pre-compute a SEARCH INDEX from it — once, lazily, cached by array identity —
  // that mirrors the server's buildSearchIndex: each station carries its ORIGINAL
  // and UPPERCASED code + name PLUS its alternate identifiers (WMO/ICAO/IATA/MGID/MMI
  // tags + every device's stationId/sourceStationId), each pre-uppercased, so a
  // per-keystroke scan is a run of cheap pre-computed compares (no 110k re-uppercasing
  // per keypress). The distinct data-source list (devices[] counts) is folded into
  // the same single pass, and a byCode map lets a server result reuse the same
  // identifier data for its "why it matched" note. Built lazily so the ~110k walk
  // never runs at boot (the palette is closed then); the first search while open pays
  // for it once, every keystroke after is cheap. Cache keyed on `allStations` identity
  // → a later boot-bundle load rebuilds it automatically with no manual bust.
  const indexCache = useRef<{ src: unknown; index: ClientIndex } | null>(null);
  const getClientIndex = useCallback((): ClientIndex => {
    if (indexCache.current?.src === allStations) return indexCache.current.index;
    const stations: ClientStationRow[] = [];
    const byCode = new Map<string, ClientStationRow>();
    const dsCount = new Map<string, number>();
    for (const s of allStations) {
      const code = String(s.stationCode ?? '');
      if (code) {
        const name = toName(s.tags?.name);
        // Named identifiers (WMO/ICAO/IATA/mgID/MMI) — shown as plain text in the row
        // AND searchable. Every value also feeds searchIdsU (uppercased), which ALSO
        // includes each device's station id / source station id: those are searchable
        // but NOT displayed (a station can carry many, and they're network-internal).
        const searchIdsU: string[] = [];
        const seenSearch = new Set<string>();
        const addSearch = (raw: unknown) => {
          const v = toName(raw).trim().toUpperCase();
          if (v && !seenSearch.has(v)) { seenSearch.add(v); searchIdsU.push(v); }
        };
        const tagIds: StationTagId[] = [];
        for (const { key, label } of ID_TAG_KEYS) {
          const value = toName(s.tags?.[key]).trim();
          if (!value) continue;
          tagIds.push({ label, value });
          addSearch(value);
        }
        for (const d of s.devices ?? []) {
          addSearch(d?.stationId);
          addSearch(d?.sourceStationId);
        }
        const row: ClientStationRow = {
          code, codeU: code.toUpperCase(), name, nameU: name.toUpperCase(),
          parameters: s.parameters ?? [], tagIds, searchIdsU,
        };
        stations.push(row);
        byCode.set(row.codeU, row);
      }
      const seen = new Set<string>();
      for (const d of s.devices ?? []) {
        const id = d?.dataSourceId;
        if (!id || seen.has(id)) continue;
        seen.add(id); // count each station once per data source
        dsCount.set(id, (dsCount.get(id) ?? 0) + 1);
      }
    }
    const networks = Array.from(dsCount.entries())
      .map(([id, count]) => ({ id, idU: id.toUpperCase(), count }))
      .sort((a, b) => b.count - a.count);
    const index: ClientIndex = { stations, byCode, networks };
    indexCache.current = { src: allStations, index };
    return index;
  }, [allStations]);

  // Warm the index the moment the palette opens — BEFORE the first keystroke. The
  // build is a ~110k-station walk (tens of ms); done lazily on first search it lands
  // as a one-time hitch on the very first character the user types. The palette's
  // entrance animation (~440ms) plus the rAF-deferred focus mean there's a window of
  // dead time after open where the user physically can't type yet — we spend it
  // building the index so the first keystroke is as instant as every one after it.
  // Deferred a frame so it never competes with the opening animation's first paint,
  // and it's a plain memoized read (idempotent): if the index is already warm this is
  // a single map lookup, and the later search reuses the exact same cached object.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => { getClientIndex(); });
    return () => cancelAnimationFrame(id);
  }, [open, getClientIndex]);

  /** RANKED scan of the in-memory index. Serves two callers:
   *   1. the INSTANT preview painted at ~0ms as the user types (the primary path);
   *   2. the FALLBACK when the server /search is down.
   *  Ranked to mirror the server (exact > prefix > earliest substring) so when the
   *  authoritative server response lands it refines rather than reshuffles the list.
   *  Returns null when the bundle is empty (nothing loaded) so the caller can tell
   *  "no local data" (→ wait for the server / surface the error) apart from
   *  "scanned and genuinely found nothing" (→ empty result set). */
  const searchClientBundle = useCallback((q: string): PaletteResult[] | null => {
    if (allStations.length === 0) return null;
    const upper = q.trim().toUpperCase();
    if (!upper) return [];
    const index = getClientIndex();

    // Networks first — typing a data-source name jumps to /network prefilled.
    const networks: PaletteResult[] = [];
    for (const n of index.networks) {
      if (networks.length >= MAX_NETWORK_RESULTS) break;
      if (n.idU.includes(upper)) networks.push(buildNetworkResult(n.id, n.count));
    }

    // Rank stations by match quality and keep only the top MAX_RESULTS — the same
    // shape as the server pass. Code beats an alternate id (WMO/ICAO/…/device station
    // id) beats name.
    //
    // This runs on EVERY keystroke, so it must stay cheap even when a broad query
    // (e.g. a single common letter) matches a large fraction of a ~110k-station
    // bundle. Rather than collect every match into one array and full-sort it
    // (O(M log M), which measured ~50–70ms per keystroke at scale — visible jank on
    // the primary interactive path), we maintain a bounded, insertion-sorted buffer
    // of just the best MAX_RESULTS seen so far: one linear O(M) scan, each survivor a
    // handful of shifts within a ≤12-slot array. Same comparator (score desc, then
    // code via localeCompare), so the top-N and its order are byte-for-byte identical
    // to the old sort — the SWR contract (local preview must not reshuffle when the
    // server response lands) is preserved. Measured ~5ms per keystroke at 110k.
    const top: Array<{ row: ClientStationRow; score: number }> = [];
    const ranksBefore = (
      a: { row: ClientStationRow; score: number },
      b: { row: ClientStationRow; score: number },
    ) => a.score > b.score || (a.score === b.score && a.row.code.localeCompare(b.row.code) < 0);
    for (const row of index.stations) {
      const codeScore = scoreMatch(row.codeU, upper);
      // Best-scoring alternate identifier (tag values + device station ids), ranked
      // just under the code (−2).
      let idScore = -1;
      const ids = row.searchIdsU;
      for (let i = 0; i < ids.length; i++) {
        const s = scoreMatch(ids[i], upper);
        if (s > idScore) idScore = s;
      }
      if (idScore >= 0) idScore -= 2;
      const nameScore = row.nameU ? scoreMatch(row.nameU, upper) - 5 : -1; // name ranks under both
      let score = codeScore;
      if (idScore > score) score = idScore;
      if (nameScore > score) score = nameScore;
      if (score < 0) continue;
      // Once the buffer is full, reject anything that can't beat the current worst
      // BEFORE allocating a candidate object — this is the cheap common case for a
      // broad query and keeps the scan allocation-light.
      if (top.length === MAX_RESULTS) {
        const worst = top[MAX_RESULTS - 1];
        if (score < worst.score || (score === worst.score && row.code.localeCompare(worst.row.code) >= 0)) continue;
      }
      const cand = { row, score };
      if (top.length < MAX_RESULTS) top.push(cand);
      // Insertion-sort the candidate into place (displacing the old worst if full).
      let j = top.length - 2;
      while (j >= 0 && ranksBefore(cand, top[j])) { top[j + 1] = top[j]; j--; }
      top[j + 1] = cand;
    }

    const stations = top.map(({ row }) =>
      buildStationResult(
        { code: row.code, name: row.name, parameters: row.parameters },
        'station',
        row.tagIds,
      ),
    );
    return [...networks, ...stations];
  }, [allStations, getClientIndex]);

  // ── Instant local results + background server revalidation (SWR) ─────────
  // The app already holds every station in memory (loadAllStations, App.tsx), so
  // the palette no longer WAITS on a network round-trip to show first results:
  // every keystroke paints ranked matches from that bundle synchronously (0ms), and
  // a debounced server search runs in the BACKGROUND to refine/correct the list
  // (authoritative ranking + stations the client bundle may be missing after a
  // restart). This is the stale-while-revalidate pattern — the user sees results
  // immediately and they only get better, never slower. A per-session cache of
  // server responses makes backspace / re-type churn instant with zero network.
  const searchSeq = useRef(0);        // guards against out-of-order responses
  const queryCache = useRef<Map<string, PaletteResult[]>>(new Map());
  // Mirror of `results` for pure, synchronous same-order comparison in applyResults
  // (reading `results` there would close over a stale value; a setState updater must
  // stay pure, so the compare can't live inside one). Kept in lock-step with the
  // state below.
  const resultsRef = useRef<PaletteResult[]>([]);
  useEffect(() => { resultsRef.current = results; }, [results]);

  // Map a server payload to palette rows (networks first, then ranked stations).
  // The displayed identifiers (WMO/ICAO/mgID/…) are looked up HERE from the client
  // index (by code) rather than carried on the wire: it keeps the server contract
  // unchanged and guarantees a server row and its instant-local twin show the
  // identical identifier line (no flicker on revalidate).
  const mapServerResults = useCallback((data: SearchResponse): PaletteResult[] => {
    const networks = (data.networks ?? [])
      .slice(0, MAX_NETWORK_RESULTS)
      .map((n) => buildNetworkResult(n.id, n.count));
    const byCode = getClientIndex().byCode;
    const stations = (data.stations ?? []).map((s) => {
      const row = byCode.get(String(s.station_code).toUpperCase());
      return buildStationResult(
        { code: s.station_code, name: s.name ?? '', parameters: s.parameters ?? [] },
        'station',
        row?.tagIds,
      );
    });
    return [...networks, ...stations];
  }, [getClientIndex]);

  // Apply a fresh result set WITHOUT stealing the user's place: only reset the
  // active row when the new set is a different shape/order than what's shown, so a
  // background refinement that agrees with the instant preview leaves the keyboard
  // selection (and the gliding highlight) exactly where the user left it.
  const applyResults = useCallback((next: PaletteResult[]) => {
    const prev = resultsRef.current;
    const sameOrder = prev.length === next.length && prev.every((r, i) => r.id === next[i].id);
    if (!sameOrder) { setActiveIndex(0); setActionIndex(0); }
    resultsRef.current = next;
    setResults(next);
  }, []);

  // INSTANT, synchronous local paint — runs on EVERY keystroke, undebounced. This is
  // what makes the palette predictive: results from the in-memory index (or the
  // session cache) appear the same frame the character is typed. Returns whether it
  // had local data to show, so the (debounced) revalidation knows whether a server
  // failure should surface an error or silently keep the shown results.
  const paintLocal = useCallback((trimmed: string): boolean => {
    const cached = queryCache.current.get(trimmed);
    const local = cached ?? searchClientBundle(trimmed);
    if (local) {
      applyResults(local);
      setStatus('ready');
      return true;
    }
    // No local data (bundle not loaded yet) — show the quiet loading line, not a
    // premature "no matches"; the pending server call is the only way to answer.
    setStatus('loading');
    return false;
  }, [searchClientBundle, applyResults]);

  // Authoritative server search — the REVALIDATE half of stale-while-revalidate.
  // Debounced (see the effect), so it coalesces a typist's burst into one request
  // and never sits on the path the user waits on. On success it refines the already-
  // painted list and caches the answer; on failure it keeps the instant results
  // (only a cold boot with no bundle AND a dead endpoint reaches the error state).
  const runServerSearch = useCallback((q: string) => {
    const trimmed = q.trim();
    if (!trimmed) {
      // Empty query → empty palette. We deliberately do NOT pre-populate favorites
      // or recent searches: an untouched (or cleared) field reads identical to a
      // freshly-opened one — results appear only in response to a query.
      searchSeq.current++; // cancel any in-flight response
      setResults([]);
      setStatus('idle');
      setActiveIndex(0);
      setActionIndex(0);
      return;
    }

    // Ensure something is on screen (Retry path calls this directly, not via the
    // keystroke effect). A cache hit is already authoritative — no network needed.
    const hadLocal = paintLocal(trimmed);
    if (queryCache.current.has(trimmed)) return;

    const seq = ++searchSeq.current;
    const baseURL = typeof window !== 'undefined' ? window.location.origin : '';
    const url = `${baseURL}/api/station-metadata/search?q=${encodeURIComponent(trimmed)}&limit=${MAX_RESULTS}`;
    fetchJSON<SearchResponse>(url, { signal: AbortSignal.timeout(8000) })
      .then((data) => {
        if (seq !== searchSeq.current) return; // a newer query superseded this one
        if (!data?.success) throw new Error('search returned unsuccessful');
        const results = mapServerResults(data);
        // Cache the authoritative answer (bounded FIFO) so re-typing it is instant.
        queryCache.current.set(trimmed, results);
        if (queryCache.current.size > QUERY_CACHE_MAX) {
          queryCache.current.delete(queryCache.current.keys().next().value as string);
        }
        applyResults(results);
        setStatus('ready');
      })
      .catch(() => {
        if (seq !== searchSeq.current) return; // stale / aborted by a newer query
        // Endpoint failed. If the instant local pass already showed something, keep
        // it (status is already 'ready'). Only when there was NO local data to show
        // (empty/unloaded bundle) do we surface the retryable error.
        if (!hadLocal) {
          setResults([]);
          setStatus('error');
          setActiveIndex(0);
          setActionIndex(0);
        }
      });
  }, [paintLocal, applyResults, mapServerResults]);

  // Split-timing search: paint LOCAL results synchronously on every keystroke (0ms,
  // the predictive feel), and debounce only the network REVALIDATION so a fast
  // typist's bursts collapse into one request. A cache hit needs no network at all.
  useEffect(() => {
    if (!open) return;
    const trimmed = query.trim();
    if (!trimmed) {
      searchSeq.current++;
      setResults([]);
      setStatus('idle');
      setActiveIndex(0);
      setActionIndex(0);
      return;
    }
    paintLocal(trimmed);                          // instant
    if (queryCache.current.has(trimmed)) return;  // authoritative already — skip fetch
    const t = setTimeout(() => runServerSearch(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, open, paintLocal, runServerSearch]);

  // Sliding highlight — a single bar glides to the active row (Apple continuity).
  const [highlight, setHighlight] = useState<{ top: number; height: number; visible: boolean }>({
    top: 0, height: 0, visible: false,
  });
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) { setHighlight(h => ({ ...h, visible: false })); return; }
    const el = list.querySelector<HTMLElement>(`[data-idx="${activeIndex}"]`);
    if (el) {
      setHighlight({ top: el.offsetTop, height: el.offsetHeight, visible: true });
      // scrollIntoView is absent in jsdom; guard so the layout effect is test-safe.
      el.scrollIntoView?.({ block: 'nearest' });
    } else {
      setHighlight(h => ({ ...h, visible: false }));
    }
  }, [activeIndex, results]);

  const runAction = useCallback((action: PaletteAction, kind: ResultKind, label: string) => {
    setOpen(false);
    if (kind === 'recent') {
      navigate(action.path);
      // Re-run the past query on the search page once it mounts.
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('quickSearch', { detail: { code: label } }));
      }, 100);
      return;
    }
    navigate(action.path);
  }, [navigate]);

  const runResult = useCallback((r: PaletteResult, ai: number) => {
    const action = r.actions[Math.min(ai, r.actions.length - 1)];
    if (action) runAction(action, r.kind, r.label);
  }, [runAction]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const active = results[activeIndex];
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, results.length - 1));
      setActionIndex(0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
      setActionIndex(0);
    } else if (e.key === 'Tab' && active) {
      // Cycle actions on the active row without stealing the text caret.
      e.preventDefault();
      const n = active.actions.length;
      setActionIndex(ai => (e.shiftKey ? (ai - 1 + n) % n : (ai + 1) % n));
    } else if (e.key === 'Enter' && active) {
      e.preventDefault();
      runResult(active, actionIndex);
    }
  };

  if (!open) return null;

  return (
    <div
      className={`cmd-palette-overlay${closing ? ' is-closing' : ''}`}
      role="presentation"
      onClick={requestClose}
    >
      <div
        ref={glassRef}
        className={`cmd-palette${results.length > 0 ? ' has-results' : ''}${closing ? ' is-closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Search stations"
        onClick={e => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="cmd-palette-input-row">
          <Search className="cmd-palette-icon" size="sm" />
          <input
            ref={inputRef}
            className="cmd-palette-input"
            type="text"
            placeholder="Search stations, networks, or data sources…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            aria-label="Search"
            aria-activedescendant={results[activeIndex]?.id}
          />
        </div>

        {/* Expand-down shell: a grid whose row animates 0fr → 1fr so the palette
            grows to the results' real height (auto-height is otherwise un-animatable),
            reading as the bar smoothly extending downward. Always mounted while the
            palette is open so the transition has a collapsed state to grow from. */}
        <div className={`cmd-palette-results-shell${results.length > 0 ? ' is-open' : ''}`}>
          <div className="cmd-palette-results-clip">
          {results.length > 0 && (
          <ul className="cmd-palette-results" role="listbox" ref={listRef}>
            <div
              className={`cmd-palette-highlight${highlight.visible ? ' is-visible' : ''}`}
              aria-hidden="true"
              style={{ transform: `translateY(${highlight.top}px)`, height: `${highlight.height}px` }}
            />
            {results.map((r, i) => (
              <li
                key={r.id}
                id={r.id}
                data-idx={i}
                role="option"
                aria-selected={i === activeIndex}
                className={`cmd-palette-item cmd-palette-item--${r.kind} ${i === activeIndex ? 'active' : ''}`}
                onClick={() => runResult(r, i === activeIndex ? actionIndex : 0)}
                onMouseEnter={() => { setActiveIndex(i); setActionIndex(0); }}
              >
                <span className={`cmd-palette-item-glyph cmd-palette-item-glyph--${r.kind}`} aria-hidden="true">
                  {r.kind === 'network' ? ACTION_ICONS.network
                    : r.kind === 'recent' ? RECENT_ICON
                    : r.kind === 'favorite' ? FAVORITE_ICON
                    : STATION_GLYPH}
                </span>
                <span className="cmd-palette-item-text">
                  <span className="cmd-palette-item-line">
                    <span className="cmd-palette-item-label">{r.label}</span>
                    {r.sublabel && <span className="cmd-palette-item-sub">{r.sublabel}</span>}
                  </span>
                  {/* Named identifiers as plain labeled text (no pills/boxes) so the
                      user can tell near-identical rows apart and know what to click. */}
                  {r.identifiers && (
                    <span className="cmd-palette-item-ids">
                      {r.identifiers.map((id) => (
                        <span key={id.label} className="cmd-palette-item-id">
                          <span className="cmd-palette-item-id__key">{id.label}:</span>
                          <span className="cmd-palette-item-id__value">{id.value}</span>
                        </span>
                      ))}
                    </span>
                  )}
                </span>
                <span className="cmd-palette-item-actions">
                  {r.actions.map((a, ai) => (
                    <MorphAction
                      key={a.key}
                      action={a}
                      resultLabel={r.label}
                      focused={i === activeIndex && ai === actionIndex}
                      onRun={() => runAction(a, r.kind, r.label)}
                      onHoverIn={() => { setActiveIndex(i); setActionIndex(ai); }}
                    />
                  ))}
                </span>
              </li>
            ))}
          </ul>
          )}
          </div>
        </div>

        {/* Footer states — mutually exclusive, only when there are no rows to show.
            `error` (endpoint down AND no client bundle) is distinct from a genuine
            empty result set: it offers a Retry instead of "no matches". While the
            first request for a query is in flight we show a quiet loading line so a
            slow cold-start doesn't read as "no matches". */}
        {query.trim() && results.length === 0 && status === 'loading' && (
          <div className="cmd-palette-empty cmd-palette-empty--loading" role="status">Searching…</div>
        )}
        {query.trim() && results.length === 0 && status === 'error' && (
          <div className="cmd-palette-empty cmd-palette-empty--error" role="alert">
            <span>Search is unavailable right now.</span>
            <button
              type="button"
              className="cmd-palette-retry"
              onClick={() => runServerSearch(query)}
            >
              Retry
            </button>
          </div>
        )}
        {query.trim() && results.length === 0 && status === 'ready' && (
          <div className="cmd-palette-empty">No stations or networks match “{query}”</div>
        )}
      </div>
    </div>
  );
};

// ── True SVG path morph: action icon → first letter of its word ───────────────
// The user chose a *literal* shape morph over a CSS crossfade: on hover each
// action's glyph physically re-forms into the FIRST LETTER of its label, and the
// REST of the word ("bservations", "tation info", …) slides in beside it. flubber
// interpolates one path `d` into another (validated for every pair below — single
// subpath throughout, exact endpoints), so `t=0` IS the rest icon and `t=1` IS the
// letter. Both are drawn as a single 2px stroke on a 24×24 grid so the morph reads
// as one continuous line reshaping — no fill-rule/counter seams mid-tween.
//
// flubber (+ its d3/earcut deps) is ~40 KB; it's lazy-imported on first hover so it
// lands in its own chunk rather than the initial bundle, and the glyph renders the
// static rest-path until the module resolves (and always, under reduced motion).
interface MorphSpec {
  /** Rest shape (t=0) — the app's real, recognisable icon (single `d`). */
  icon: string;
  /** Morph target (t=1) — a letterform the icon reshapes toward before it hands
   *  off to the real text. Not shown on its own; it's the "becoming text" cue. */
  letter: string;
}
// The `icon` fields are the app's REAL ACTION_ICONS geometry, flattened to a single
// `d` string (<circle>→arc pair, <rect>→h/v lines, each <path> kept as its own
// M-subpath). flubber preserves those subpaths at t=0, so the RESTING glyph is the
// full, recognisable, app-consistent icon. On hover it reshapes toward `letter`
// while collapsing + fading, and the FULL word (uniform Inter text) expands into its
// place — so the settled state is one clean word with no SVG-letter/text seam.
const MORPH: Record<ActionKey, MorphSpec> = {
  // axis + trend line → 'O'
  observations: {
    icon: 'M3 3 v18 h18 M7 14 l3.5 -4 l3 3 L21 6',
    letter: 'M12 4 C7.6 4 5 7.6 5 12 C5 16.4 7.6 20 12 20 C16.4 20 19 16.4 19 12 C19 7.6 16.4 4 12 4 Z',
  },
  // info circle + "i" → 'S'
  station: {
    icon: 'M3 12 a9 9 0 1 0 18 0 a9 9 0 1 0 -18 0 M12 11 v5 M12 7.5 h0.01',
    letter: 'M17 7 C16 4.5 12 4 9.5 5.5 C6.5 7.3 7.5 10.5 11 11.5 C15 12.7 15.8 15.8 12.5 17.5 C10 18.8 6.8 18 6 15.5',
  },
  // calendar → 'A'
  availability: {
    icon: 'M3 4.5 h18 v16 h-18 Z M3 9 h18 M8 3 v3 M16 3 v3 M8 13 h2 M14 13 h2 M8 17 h2',
    letter: 'M5 20 L12 4 L19 20 M8.2 14 H15.8',
  },
  // node graph → 'N'
  network: {
    icon: 'M9.6 5 a2.4 2.4 0 1 0 4.8 0 a2.4 2.4 0 1 0 -4.8 0 M2.6 18 a2.4 2.4 0 1 0 4.8 0 a2.4 2.4 0 1 0 -4.8 0 M16.6 18 a2.4 2.4 0 1 0 4.8 0 a2.4 2.4 0 1 0 -4.8 0 M12 7.4 v3.6 M12 11 l-5 4.6 M12 11 l5 4.6',
    letter: 'M6 20 V4 L18 20 V4',
  },
  // magnifier → 'S'
  search: {
    icon: 'M4 11 a7 7 0 1 0 14 0 a7 7 0 1 0 -14 0 M20 20 l-4 -4',
    letter: 'M17 7 C16 4.5 12 4 9.5 5.5 C6.5 7.3 7.5 10.5 11 11.5 C15 12.7 15.8 15.8 12.5 17.5 C10 18.8 6.8 18 6 15.5',
  },
};

// flubber is loaded once, lazily, and shared across every glyph instance.
type Interpolator = (from: string, to: string, opts?: { maxSegmentLength?: number }) => (t: number) => string;
let flubberInterpolate: Interpolator | null = null;
let flubberPromise: Promise<Interpolator | null> | null = null;
const loadFlubber = (): Promise<Interpolator | null> => {
  if (flubberInterpolate) return Promise.resolve(flubberInterpolate);
  if (!flubberPromise) {
    flubberPromise = import('flubber')
      .then(m => { flubberInterpolate = m.interpolate as Interpolator; return flubberInterpolate; })
      .catch(() => null); // offline / chunk error → stay on the static icon, no crash
  }
  return flubberPromise;
};

/**
 * Cubic-bézier easing evaluator — the same math CSS uses for its timing
 * functions. Returns a function mapping linear time (0→1) to an eased value
 * (0→1) via Newton-Raphson (with a bisection fallback). This lets the JS path
 * tween ride the EXACT curve of the CSS transitions it animates alongside, so
 * the glyph reshape and the word bloom share one velocity profile.
 */
function cubicBezier(x1: number, y1: number, x2: number, y2: number): (t: number) => number {
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number) => ((ay * t + by) * t + cy) * t;
  const sampleDX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  const solveX = (x: number) => {
    let t = x;
    for (let i = 0; i < 8; i++) {
      const err = sampleX(t) - x;
      if (Math.abs(err) < 1e-4) return t;
      const d = sampleDX(t);
      if (Math.abs(d) < 1e-6) break;
      t -= err / d;
    }
    let lo = 0, hi = 1; t = x;
    for (let i = 0; i < 20; i++) {
      const err = sampleX(t) - x;
      if (Math.abs(err) < 1e-4) break;
      if (err > 0) hi = t; else lo = t;
      t = (lo + hi) / 2;
    }
    return t;
  };
  return (x: number) => sampleY(solveX(Math.min(1, Math.max(0, x))));
}

/* Mirror the CSS --morph-ms / --liquid declared on .cmd-palette-item-actions, so
   the JS glyph-path tween, the CSS word bloom, and the sibling-pill reflow all
   share one duration + one curve and settle together as a single liquid motion. */
const MORPH_MS = 300;
const LIQUID = cubicBezier(0.22, 1, 0.36, 1);

/**
 * A single 24×24 stroke path that tweens between its rest icon and its hover
 * letter. Driven by an interruptible, TIME-BASED rAF loop toward a target
 * progress (0 or 1) — frame-rate independent, eased on the shared LIQUID curve —
 * so hover-in / hover-out mid-flight reverse smoothly instead of snapping, and
 * the shape morph stays locked to the CSS width/scale morph. Falls back to the
 * static endpoint path when flubber hasn't loaded or motion is reduced.
 */
const MorphGlyph = ({ actionKey, hovered }: { actionKey: ActionKey; hovered: boolean }) => {
  const { icon, letter } = MORPH[actionKey];
  const [d, setD] = useState(icon);
  const interpRef = useRef<((t: number) => string) | null>(null);
  const rafRef = useRef<number | null>(null);
  const progRef = useRef(0); // current tween position 0→1

  useEffect(() => {
    const reduce = prefersReducedMotion();
    const target = hovered ? 1 : 0;

    // Reduced motion (or SSR/no-rAF): jump straight to the endpoint shape.
    if (reduce || typeof requestAnimationFrame !== 'function') {
      progRef.current = target;
      setD(target === 1 ? letter : icon);
      return;
    }

    let cancelled = false;
    loadFlubber().then(fn => {
      if (cancelled) return;
      if (!fn) { setD(target === 1 ? letter : icon); return; } // load failed
      // Rebuild the interpolator for this icon/letter pair once. Building samples the
      // paths via SVGPathElement.getTotalLength(), which a non-browser runtime (jsdom,
      // some embedded webviews) may not implement — degrade to the static endpoint
      // shape instead of letting the throw escape as an unhandled rejection.
      if (!interpRef.current) {
        try {
          interpRef.current = fn(icon, letter, { maxSegmentLength: 2 });
        } catch {
          setD(target === 1 ? letter : icon);
          return;
        }
      }
      const interp = interpRef.current;
      if (!interp) return; // unreachable after the guard above; narrows for the closure

      // Time-based, frame-rate-independent tween. progRef holds LINEAR progress
      // (0→1); each frame we advance it by elapsed/MORPH_MS toward the target, then
      // apply the shared LIQUID curve before sampling the path — the SAME duration
      // and easing the CSS word bloom uses, so shape + width + reflow move as one.
      // Tracking linear (un-eased) progress keeps a mid-flight hover reversal smooth.
      let last: number | null = null;
      const step = (now: number) => {
        if (last === null) last = now;
        const dt = now - last;
        last = now;
        const p = progRef.current;
        const dir = Math.sign(target - p);
        const nextLinear = p + dir * (dt / MORPH_MS);
        const done = dir === 0 || (dir > 0 ? nextLinear >= target : nextLinear <= target);
        progRef.current = done ? target : nextLinear;
        setD(interp(LIQUID(progRef.current)));
        rafRef.current = done ? null : requestAnimationFrame(step);
      };
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(step);
    }).catch(() => { /* a glyph tween must never crash (or noise up) the palette */ });

    return () => {
      cancelled = true;
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
  }, [hovered, icon, letter]);

  return (
    <svg className="cmd-palette-action__glyph" width="15" height="15" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d={d} />
    </svg>
  );
};

/**
 * One action pill. At rest it shows only the morphing glyph. On hover the glyph
 * reshapes toward a letterform while it collapses + fades, and the FULL label —
 * one uniform Inter word — expands into its place (CSS 0fr→1fr auto-width, the same
 * trick the results panel uses). Glyph and word cross in the same spot, so the
 * settled state is a single clean word with no SVG-letter/text seam. Keyboard focus
 * is mirrored by `focused` so hover and ⤥-focus share one visual language.
 */
const MorphAction = ({
  action, resultLabel, focused, onRun, onHoverIn,
}: {
  action: PaletteAction;
  resultLabel: string;
  focused: boolean;
  onRun: () => void;
  onHoverIn: () => void;
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      className={`cmd-palette-action${focused ? ' is-focused' : ''}`}
      aria-label={`${action.label} — ${resultLabel}`}
      tabIndex={-1}
      onClick={e => { e.stopPropagation(); onRun(); }}
      onMouseEnter={() => { setHovered(true); onHoverIn(); }}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Glyph morphs on HOVER only. Keyboard focus keeps the clean blue-tint/
          enlarge affordance (below): the row's focused action always shows its
          icon, and only pointer-hover swaps in the word. */}
      <MorphGlyph actionKey={action.key} hovered={hovered} />
      {/* The full label as ONE uniform text word — expands into the glyph's place
          as the glyph collapses away. Wrapped so the grid track can collapse it (a
          bare text node isn't a grid child). */}
      <span className="cmd-palette-action__word" aria-hidden="true">
        <span>{action.label}</span>
      </span>
    </button>
  );
};

// ── Icons (24×24, currentColor, stroke) ────────────────────────────────
// The shared icons/ module is not on this branch; inline SVGs keep the palette
// self-contained and match the stroke-width/viewBox convention used elsewhere.
const ACTION_ICONS: Record<ActionKey, ReactElement> = {
  // Observations — line chart / activity
  observations: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18" /><path d="M7 14l3.5-4 3 3L21 6" />
    </svg>
  ),
  // Station info — info circle
  station: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 7.5h.01" />
    </svg>
  ),
  // Availability — calendar / temporal coverage
  availability: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v3M16 3v3" /><path d="M8 13h2M14 13h2M8 17h2" />
    </svg>
  ),
  // Network — shared nodes / hierarchy
  network: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="5" r="2.4" /><circle cx="5" cy="18" r="2.4" /><circle cx="19" cy="18" r="2.4" />
      <path d="M12 7.4v3.6M12 11l-5 4.6M12 11l5 4.6" />
    </svg>
  ),
  // Search (recent) — magnifier
  search: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" />
    </svg>
  ),
};

const STATION_GLYPH = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 21s-6.5-5.6-6.5-10a6.5 6.5 0 1 1 13 0c0 4.4-6.5 10-6.5 10Z" /><circle cx="12" cy="11" r="2.2" />
  </svg>
);

const RECENT_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 4v4h4" /><path d="M12 8v4l3 2" />
  </svg>
);

const FAVORITE_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
    <path d="M12 3.5l2.6 5.3 5.9.86-4.25 4.14 1 5.87L12 17.9l-5.25 2.77 1-5.87L3.5 9.66l5.9-.86L12 3.5Z" />
  </svg>
);
